import React, { useState, useEffect } from 'react';
import { getToken, getUser } from '../utils/storage';
import CustomerLayout from '../components/CustomerLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Monitor, MessageSquare, Image as ImageIcon, Eye, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerDashboard = () => {
  const { t } = useTranslation();
  const [screens, setScreens] = useState([]);
  const [organizationName, setOrganizationName] = useState('');
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [refreshingScreen, setRefreshingScreen] = useState(null);
  const [stats, setStats] = useState({
    totalScreens: 0,
    activeScreens: 0,
    totalMessages: 0,
    totalImages: 0
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = getToken();
      
      // Fetch organization info
      const orgRes = await axios.get(`${API}/organizations/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrganizationName(orgRes.data.name);
      
      // Fetch screens (handle empty arrays gracefully)
      const screensRes = await axios.get(`${API}/screens`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const screensData = Array.isArray(screensRes.data) ? screensRes.data : [];
      setScreens(screensData);
      
      // Fetch messages count (handle empty arrays gracefully)
      const messagesRes = await axios.get(`${API}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const messagesData = Array.isArray(messagesRes.data) ? messagesRes.data : [];
      
      // Fetch images count (handle empty arrays gracefully)
      const imagesRes = await axios.get(`${API}/images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const imagesData = Array.isArray(imagesRes.data) ? imagesRes.data : [];
      
      setStats({
        totalScreens: screensData.length,
        activeScreens: screensData.filter(s => s.is_active).length,
        totalMessages: messagesData.length,
        totalImages: imagesData.length
      });
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      toast.error(t('customerDashboard.errorLoadingDashboard'));
    }
  };

  // Refresh all screens
  const handleRefreshAllScreens = async () => {
    setRefreshingAll(true);
    try {
      const token = getToken();
      const response = await axios.post(`${API}/screens/refresh-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Oppdateringssignal sendt til ${response.data.screen_count} skjermer`);
    } catch (error) {
      toast.error('Kunne ikke sende oppdatering til skjermene');
    } finally {
      setTimeout(() => setRefreshingAll(false), 1000);
    }
  };

  // Refresh single screen
  const handleRefreshScreen = async (e, screenId) => {
    e.stopPropagation(); // Prevent opening the screen preview
    setRefreshingScreen(screenId);
    try {
      const token = getToken();
      await axios.post(`${API}/screens/${screenId}/refresh`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Oppdateringssignal sendt til skjermen');
    } catch (error) {
      toast.error('Kunne ikke sende oppdatering');
    } finally {
      setTimeout(() => setRefreshingScreen(null), 1000);
    }
  };

  return (
    <CustomerLayout>
      {/* Customer name aligned with sidebar logo height */}
      <div style={{ 
        marginTop: '0', 
        marginBottom: '2.5rem',
        paddingTop: '1rem',
        paddingBottom: '1.5rem'
      }}>
        <h1 style={{ 
          margin: 0,
          fontSize: '2rem',
          fontWeight: 'bold',
          color: '#ffffff'
        }}>
          {organizationName || t('customerDashboard.loading')}
        </h1>
      </div>
      
      {/* Stats Cards - lifted 37px to align with "Oversikt" menu item */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '1.5rem', 
        marginTop: '-37px',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          background: 'rgba(80, 227, 194, 0.1)',
          border: '1px solid rgba(80, 227, 194, 0.3)',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <Monitor size={24} color="#50E3C2" />
            <h3 style={{ margin: 0, fontSize: '0.9rem', color: '#ffffff' }}>{t('customerDashboard.screens')}</h3>
          </div>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0, color: '#ffffff' }}>
            {stats.activeScreens}/{stats.totalScreens}
          </p>
          <p style={{ fontSize: '0.85rem', color: '#ffffff', margin: 0 }}>{t('customerDashboard.activeScreens')}</p>
        </div>

        <div style={{
          background: 'rgba(80, 227, 194, 0.1)',
          border: '1px solid rgba(80, 227, 194, 0.3)',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <MessageSquare size={24} color="#50E3C2" />
            <h3 style={{ margin: 0, fontSize: '0.9rem', color: '#ffffff' }}>{t('customerDashboard.messages')}</h3>
          </div>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0, color: '#ffffff' }}>{stats.totalMessages}</p>
          <p style={{ fontSize: '0.85rem', color: '#ffffff', margin: 0 }}>{t('customerDashboard.totalCount')}</p>
        </div>

        <div style={{
          background: 'rgba(80, 227, 194, 0.1)',
          border: '1px solid rgba(80, 227, 194, 0.3)',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <ImageIcon size={24} color="#50E3C2" />
            <h3 style={{ margin: 0, fontSize: '0.9rem', color: '#ffffff' }}>{t('customerDashboard.images')}</h3>
          </div>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0, color: '#ffffff' }}>{stats.totalImages}</p>
          <p style={{ fontSize: '0.85rem', color: '#ffffff', margin: 0 }}>{t('customerDashboard.inGallery')}</p>
        </div>
      </div>

      {/* Screen Previews */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0 }}>{t('customerDashboard.yourScreens')}</h2>
        {screens.length > 0 && (
          <button
            className="btn btn-secondary"
            onClick={handleRefreshAllScreens}
            disabled={refreshingAll}
            title="Oppdater alle skjermer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: refreshingAll ? 'rgba(80, 227, 194, 0.2)' : undefined
            }}
          >
            <RefreshCw 
              size={18} 
              className={refreshingAll ? 'spin' : ''}
            />
            <span>{refreshingAll ? 'Sender...' : 'Oppdater alle'}</span>
          </button>
        )}
      </div>
      {screens.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem', 
          background: 'rgba(255,255,255,0.05)', 
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.1)'
        }}>
          <Monitor size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
          <p style={{ color: '#999', marginBottom: '1rem' }}>{t('customerDashboard.noScreensYet')}</p>
          <button 
            className="btn btn-primary"
            onClick={() => window.location.href = '/screens'}
          >
            {t('customerDashboard.createFirstScreen')}
          </button>
        </div>
      ) : (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))', 
          gap: 'clamp(0.75rem, 1.5vw, 1rem)' 
        }}>
          {screens.map(screen => (
            <div 
              key={screen.id}
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '12px',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.3s'
              }}
              onClick={() => window.open(screen.display_url || `/api/display-es5/${screen.qr_code_id}`, '_blank')}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#50E3C2';
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {/* Preview placeholder */}
              <div style={{
                width: '100%',
                height: '180px',
                background: 'linear-gradient(135deg, rgba(80,227,194,0.1) 0%, rgba(66,156,136,0.1) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
              }}>
                <Monitor size={64} style={{ color: '#ffffff', opacity: 0.3 }} />
                {/* Refresh button */}
                <button
                  onClick={(e) => handleRefreshScreen(e, screen.id)}
                  disabled={refreshingScreen === screen.id}
                  title="Oppdater denne skjermen"
                  style={{
                    position: 'absolute',
                    top: '0.75rem',
                    left: '0.75rem',
                    background: refreshingScreen === screen.id ? 'rgba(80, 227, 194, 0.3)' : 'rgba(0, 0, 0, 0.5)',
                    border: '1px solid rgba(80, 227, 194, 0.5)',
                    borderRadius: '8px',
                    padding: '0.5rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s'
                  }}
                >
                  <RefreshCw 
                    size={18} 
                    color="#50E3C2" 
                    className={refreshingScreen === screen.id ? 'spin' : ''}
                  />
                </button>
                <div style={{
                  position: 'absolute',
                  top: '0.75rem',
                  right: '0.75rem',
                  background: screen.is_active ? '#50E3C2' : '#666',
                  color: screen.is_active ? '#0F0F0F' : '#fff',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}>
                  {screen.is_active ? t('customerDashboard.active') : t('customerDashboard.inactive')}
                </div>
              </div>
              
              {/* Screen info */}
              <div style={{ padding: '1.25rem' }}>
                <h3 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.1rem', color: '#ffffff' }}>
                  {screen.screen_name}
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <img 
                    src={screen.orientation === 'landscape' ? '/landscape-screen-icon.svg' : '/portrait-screen-icon.svg'} 
                    alt={screen.orientation === 'landscape' ? t('customerDashboard.landscape') : t('customerDashboard.portrait')}
                    style={{ width: '20px', height: '20px', filter: 'brightness(0) invert(1)', opacity: 0.3 }}
                  />
                  <span style={{ color: '#ffffff', fontSize: '0.85rem' }}>
                    {screen.orientation === 'landscape' ? t('customerDashboard.landscape') : t('customerDashboard.portrait')}
                  </span>
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem',
                  padding: '0.75rem',
                  background: 'rgba(0,0,0,0.2)',
                  borderRadius: '8px',
                  fontSize: '0.85rem'
                }}>
                  <Eye size={16} color="#50E3C2" />
                  <span style={{ color: '#50E3C2', fontWeight: 'bold' }}>
                    {t('customerDashboard.clickToPreview')}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </CustomerLayout>
  );
};

export default CustomerDashboard;
